import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { cod } from '@/lib/api';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, ArrowRight,
  Plus, CheckCircle, Clock, AlertTriangle, RefreshCw,
  Receipt, CreditCard, Banknote, Wallet, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('pt-BR');
}

function StatusBadge({ status }) {
  const colors = {
    OPEN: 'bg-yellow-100 text-yellow-700',
    PARTIAL: 'bg-blue-100 text-blue-700',
    PAID: 'bg-emerald-100 text-emerald-700',
    CANCELLED: 'bg-slate-100 text-slate-500',
  };
  const labels = {
    OPEN: 'Aberto',
    PARTIAL: 'Parcial',
    PAID: 'Pago',
    CANCELLED: 'Cancelado',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-slate-100'}`}>
      {labels[status] || status}
    </span>
  );
}

function TypeBadge({ type }) {
  const colors = {
    SALE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    COD_REPASS: 'bg-orange-50 text-orange-700 border-orange-200',
    DRIVER_FEE: 'bg-blue-50 text-blue-700 border-blue-200',
    ADJUST: 'bg-purple-50 text-purple-700 border-purple-200',
    REFUND: 'bg-red-50 text-red-700 border-red-200',
  };
  const labels = {
    SALE: 'Venda',
    COD_REPASS: 'Repasse COD',
    DRIVER_FEE: 'Taxa Entrega',
    ADJUST: 'Ajuste',
    REFUND: 'Reembolso',
  };
  return (
    <span className={`px-2 py-1 rounded border text-xs font-medium ${colors[type] || 'bg-slate-50'}`}>
      {labels[type] || type}
    </span>
  );
}

// ==================== ACCOUNTS RECEIVABLE TAB ====================
function AccountsReceivableTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedAR, setSelectedAR] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('PIX');
  const [paying, setPaying] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.ar_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await cod.finance.ar.list(params);
      setItems(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar contas a receber');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenPay = (ar) => {
    setSelectedAR(ar);
    setPayAmount(ar.amount - ar.received_amount);
    setShowPayDialog(true);
  };

  const handlePay = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    setPaying(true);
    try {
      await cod.finance.ar.receive(selectedAR.id, {
        amount: parseFloat(payAmount),
        method: payMethod,
        reference: null
      });
      toast.success('Recebimento registrado!');
      setShowPayDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao registrar recebimento');
    } finally {
      setPaying(false);
    }
  };

  // Summary
  const totalOpen = items.filter(i => i.status === 'OPEN').reduce((sum, i) => sum + i.amount - i.received_amount, 0);
  const totalPartial = items.filter(i => i.status === 'PARTIAL').reduce((sum, i) => sum + i.amount - i.received_amount, 0);
  const totalPaid = items.filter(i => i.status === 'PAID').reduce((sum, i) => sum + i.received_amount, 0);

  return (
    <div className="space-y-6" data-testid="ar-tab">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">Em Aberto</p>
          <p className="text-2xl font-bold text-yellow-700">{formatCurrency(totalOpen)}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">Parcial</p>
          <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalPartial)}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">Recebido</p>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalPaid)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Filter size={16} className="text-slate-400" />
        <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ar-type-filter">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="SALE">Venda</SelectItem>
            <SelectItem value="COD_REPASS">Repasse COD</SelectItem>
            <SelectItem value="ADJUST">Ajuste</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ar-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="OPEN">Aberto</SelectItem>
            <SelectItem value="PARTIAL">Parcial</SelectItem>
            <SelectItem value="PAID">Pago</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <TrendingUp className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500">Nenhuma conta a receber encontrada</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Descrição</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Valor</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Recebido</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Saldo</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Vencimento</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((ar) => (
                <tr key={ar.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ar-row-${ar.id}`}>
                  <td className="px-4 py-3"><TypeBadge type={ar.ar_type} /></td>
                  <td className="px-4 py-3 text-sm text-slate-700">{ar.description || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(ar.amount)}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-600">{formatCurrency(ar.received_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-slate-900">
                    {formatCurrency(ar.amount - ar.received_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-slate-600">{formatDate(ar.due_date)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={ar.status} /></td>
                  <td className="px-4 py-3 text-center">
                    {ar.status !== 'PAID' && ar.status !== 'CANCELLED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenPay(ar)}
                        data-testid={`receive-ar-${ar.id}`}
                      >
                        <DollarSign size={14} className="mr-1" />
                        Receber
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* Pay Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Recebimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedAR && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm"><span className="text-slate-500">Descrição:</span> {selectedAR.description}</p>
                <p className="text-sm"><span className="text-slate-500">Valor Total:</span> {formatCurrency(selectedAR.amount)}</p>
                <p className="text-sm"><span className="text-slate-500">Já Recebido:</span> {formatCurrency(selectedAR.received_amount)}</p>
                <p className="text-sm font-bold"><span className="text-slate-500">Saldo:</span> {formatCurrency(selectedAR.amount - selectedAR.received_amount)}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Valor a Receber</label>
              <Input
                type="number"
                step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                data-testid="ar-pay-amount"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Método</label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="ar-pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                  <SelectItem value="CARTAO">Cartão</SelectItem>
                  <SelectItem value="TRANSFERENCIA">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>Cancelar</Button>
            <Button onClick={handlePay} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700">
              {paying ? 'Registrando...' : 'Registrar Recebimento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== ACCOUNTS PAYABLE TAB ====================
function AccountsPayableTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedAP, setSelectedAP] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('PIX');
  const [paying, setPaying] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.ap_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await cod.finance.ap.list(params);
      setItems(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenPay = (ap) => {
    setSelectedAP(ap);
    setPayAmount(ap.amount - ap.paid_amount);
    setShowPayDialog(true);
  };

  const handlePay = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    setPaying(true);
    try {
      await cod.finance.ap.pay(selectedAP.id, {
        amount: parseFloat(payAmount),
        method: payMethod,
        reference: null
      });
      toast.success('Pagamento registrado!');
      setShowPayDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao registrar pagamento');
    } finally {
      setPaying(false);
    }
  };

  // Summary
  const totalOpen = items.filter(i => i.status === 'OPEN').reduce((sum, i) => sum + i.amount - i.paid_amount, 0);
  const totalPaid = items.filter(i => i.status === 'PAID').reduce((sum, i) => sum + i.paid_amount, 0);

  return (
    <div className="space-y-6" data-testid="ap-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">A Pagar</p>
          <p className="text-2xl font-bold text-red-700">{formatCurrency(totalOpen)}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">Pago</p>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalPaid)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Filter size={16} className="text-slate-400" />
        <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ap-type-filter">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="DRIVER_FEE">Taxa Entrega</SelectItem>
            <SelectItem value="REFUND">Reembolso</SelectItem>
            <SelectItem value="ADJUST">Ajuste</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ap-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="OPEN">Aberto</SelectItem>
            <SelectItem value="PAID">Pago</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <TrendingDown className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500">Nenhuma conta a pagar encontrada</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Descrição</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Valor</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Pago</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Saldo</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Vencimento</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((ap) => (
                <tr key={ap.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ap-row-${ap.id}`}>
                  <td className="px-4 py-3"><TypeBadge type={ap.ap_type} /></td>
                  <td className="px-4 py-3 text-sm text-slate-700">{ap.description || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(ap.amount)}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-600">{formatCurrency(ap.paid_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-red-600">
                    {formatCurrency(ap.amount - ap.paid_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-slate-600">{formatDate(ap.due_date)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={ap.status} /></td>
                  <td className="px-4 py-3 text-center">
                    {ap.status !== 'PAID' && ap.status !== 'CANCELLED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenPay(ap)}
                        data-testid={`pay-ap-${ap.id}`}
                      >
                        <DollarSign size={14} className="mr-1" />
                        Pagar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* Pay Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedAP && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm"><span className="text-slate-500">Descrição:</span> {selectedAP.description}</p>
                <p className="text-sm"><span className="text-slate-500">Valor Total:</span> {formatCurrency(selectedAP.amount)}</p>
                <p className="text-sm"><span className="text-slate-500">Já Pago:</span> {formatCurrency(selectedAP.paid_amount)}</p>
                <p className="text-sm font-bold"><span className="text-slate-500">Saldo:</span> {formatCurrency(selectedAP.amount - selectedAP.paid_amount)}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Valor a Pagar</label>
              <Input
                type="number"
                step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                data-testid="ap-pay-amount"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Método</label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="ap-pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                  <SelectItem value="TRANSFERENCIA">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>Cancelar</Button>
            <Button onClick={handlePay} disabled={paying} className="bg-red-600 hover:bg-red-700">
              {paying ? 'Registrando...' : 'Registrar Pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== LEDGER TAB ====================
function LedgerTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountFilter, setAccountFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (accountFilter) params.account = accountFilter;
      if (typeFilter) params.entry_type = typeFilter;
      const res = await cod.finance.ledger(params);
      setEntries(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar lançamentos');
    } finally {
      setLoading(false);
    }
  }, [accountFilter, typeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Balances by account
  const balances = entries.reduce((acc, entry) => {
    const account = entry.account;
    if (!acc[account]) acc[account] = 0;
    const amount = parseFloat(entry.amount) || 0;
    if (entry.entry_type === 'DEBIT') {
      acc[account] += amount;
    } else {
      acc[account] -= amount;
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="ledger-tab">
      {/* Account Balances */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(balances).map(([account, balance]) => (
          <div key={account} className="bg-white border border-slate-200 rounded-xl p-3">
            <p className="text-xs text-slate-500 mb-1">{account.replace('_', ' ')}</p>
            <p className={`text-lg font-bold ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatCurrency(balance)}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Filter size={16} className="text-slate-400" />
        <Select value={accountFilter || "all"} onValueChange={(v) => setAccountFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ledger-account-filter">
            <SelectValue placeholder="Conta" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="CASH">Caixa</SelectItem>
            <SelectItem value="BANK">Banco</SelectItem>
            <SelectItem value="ONLINE_GATEWAY">Gateway</SelectItem>
            <SelectItem value="COD_HOLD">COD Hold</SelectItem>
            <SelectItem value="DRIVER_CLEARING">Clearing</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ledger-type-filter">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="DEBIT">Débito</SelectItem>
            <SelectItem value="CREDIT">Crédito</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Carregando...</div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Receipt className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500">Nenhum lançamento encontrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Data</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Conta</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Valor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Descrição</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Referência</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${entry.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {new Date(entry.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${entry.entry_type === 'DEBIT' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {entry.entry_type === 'DEBIT' ? 'Entrada' : 'Saída'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-700">{entry.account?.replace('_', ' ')}</td>
                  <td className={`px-4 py-3 text-sm text-right font-bold ${entry.entry_type === 'DEBIT' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {entry.entry_type === 'DEBIT' ? '+' : '-'}{formatCurrency(entry.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{entry.description || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{entry.reference_type} {entry.reference_id?.slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export function CODFinancePage() {
  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="cod-finance-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Financeiro COD
          </h1>
          <p className="text-slate-600">Contas a Receber, a Pagar e Livro Caixa do fluxo COD</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="ar" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="ar" data-testid="tab-ar">
              <TrendingUp size={16} className="mr-2" />
              A Receber
            </TabsTrigger>
            <TabsTrigger value="ap" data-testid="tab-ap">
              <TrendingDown size={16} className="mr-2" />
              A Pagar
            </TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">
              <Receipt size={16} className="mr-2" />
              Livro Caixa
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ar" className="mt-6">
            <AccountsReceivableTab />
          </TabsContent>

          <TabsContent value="ap" className="mt-6">
            <AccountsPayableTab />
          </TabsContent>

          <TabsContent value="ledger" className="mt-6">
            <LedgerTab />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}

export default CODFinancePage;
